import { Link as RouterLink } from "@tanstack/react-router";
import type { ComponentPropsWithoutRef } from "react";

/**
 * Drop-in replacement for `next/link`, keeping the `href` prop name.
 *
 * Two reasons this exists rather than using `@tanstack/react-router`'s `Link`
 * directly at every call site:
 *
 *  1. Roughly half of the `href`s in this app are not app routes — `tel:`,
 *     `mailto:`, `https://wa.me/...`, `task.issueUrl`, `account.driveLink` —
 *     and several are decided at runtime (`h.accountId ? "/customers/..." :
 *     "/tasks"`). Router `Link` is for in-app navigation and would try to
 *     resolve those as paths. Here anything with a scheme, a protocol-relative
 *     prefix, or a bare `#` renders as a plain `<a>` with the right
 *     `target`/`rel`, and everything else goes through the router.
 *  2. It keeps `href` as the prop name, so the ~30 existing call sites did not
 *     have to be rewritten into `to` + `params` pairs.
 *
 * The cost is that `href` is a plain `string`, so these links are not checked
 * against the generated route tree. Router `Link` (with `to`/`params`) is still
 * available and preferred for new static links where the type safety is free.
 */
const EXTERNAL = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

export type LinkProps = Omit<ComponentPropsWithoutRef<"a">, "href"> & {
  href?: string;
};

export function Link({ href, ...props }: LinkProps) {
  // `undefined` href is used as "not actionable" by ContactAction and friends;
  // <a> without href is correct there (not focusable, not a link).
  if (href === undefined) return <a {...props} />;

  if (EXTERNAL.test(href) || href.startsWith("#")) {
    const offSite = /^(?:https?:|\/\/)/i.test(href);
    return (
      <a
        href={href}
        {...(offSite ? { target: "_blank", rel: "noreferrer" } : {})}
        {...props}
      />
    );
  }

  // "/tasks?task=abc" -> to + search, so a query string survives the router.
  const q = href.indexOf("?");
  if (q === -1) return <RouterLink to={href} {...props} />;
  const search = Object.fromEntries(new URLSearchParams(href.slice(q + 1)));
  return <RouterLink to={href.slice(0, q)} search={search as never} {...props} />;
}

export default Link;
